import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Folder, type LucideIcon as Icon, MoreHorizontal, Share2, Trash2 } from 'lucide-react' // Add type keyword
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar'

import { Link, useLocation } from 'react-router-dom' // Import useLocation
import { useTranslation } from 'react-i18next'

interface ManagementItem {
	key: string
	url: string
	icon: Icon
}

interface NavManagementProps {
	items: ManagementItem[];
	isAdmin: boolean;
}

export function NavManagement({ items, isAdmin }: NavManagementProps) {
	const { t } = useTranslation()
	const { isMobile } = useSidebar()
	const location = useLocation() // Get current location

	if (!isAdmin) {
		return null;
	}

	return (
		<SidebarGroup className='group-data-[collapsible=icon]:hidden'>
			<SidebarGroupLabel className='mt-4 mb-2 text-xs text-muted-foreground'>
				{t('sidebar.management.groupLabel')}
			</SidebarGroupLabel>
			<SidebarMenu>
				{items.map((item) => {
					const isActive = location.pathname === item.url || (item.url !== '/dashboard' && location.pathname.startsWith(item.url));
					return (
						<SidebarMenuItem key={item.key}>
							<SidebarMenuButton asChild isActive={isActive}>
								<Link to={item.url}>
									<item.icon />
									<span>{t(item.key)}</span>
							</Link>
						</SidebarMenuButton>
						{item.key !== 'sidebar.users' && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<SidebarMenuAction showOnHover className='data-[state=open]:bg-accent rounded-sm'>
										<MoreHorizontal />
										<span className='sr-only'>{t('sidebar.management.moreAction')}</span>
									</SidebarMenuAction>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									className='w-24 rounded-lg'
									side={isMobile ? 'bottom' : 'right'}
									align={isMobile ? 'end' : 'start'}
								>
									<DropdownMenuItem>
										<Folder />
										<span>{t('sidebar.management.openAction')}</span>
									</DropdownMenuItem>
									<DropdownMenuItem>
										<Share2 />
										<span>{t('sidebar.management.shareAction')}</span>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem variant='destructive'>
										<Trash2 />
										<span>{t('sidebar.management.deleteAction')}</span>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							)}
						</SidebarMenuItem>
					)
				})}
			</SidebarMenu>
		</SidebarGroup>
	)
}
